import { Component, ElementRef, forwardRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';

import { show_popup, TITLE_ERROR } from '@shared/utility/popup-error';

import { PasswordInfo } from '@core/model/password-info';
import { ErrorStateMatcher } from '@angular/material/core';
import { retryWithDelay } from '@core/operators/retry-delay';
import { PasswordVerificationService } from './password-verification.service';
import { ValidatorService } from '../validator.service';

export class PasswordErrorStateMatcher extends ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return control.dirty && form.invalid;
  }
}

@Component({
  selector: 'app-rapi-password',
  templateUrl: './rapi-password.component.html',
  styleUrls: ['./rapi-password.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RapiPasswordComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RapiPasswordComponent),
      multi: true,
    },
  ],
})
export class RapiPasswordComponent implements OnInit, ControlValueAccessor, OnDestroy {
  form: FormGroup;
  mismatchMsg: string;
  passwordErrorStateMatcher = new PasswordErrorStateMatcher();
  subscriptions: Subscription[] = [];
  @ViewChild('passwordInput', { static: false }) passwordInput: ElementRef;

  constructor(
    private formBuilder: FormBuilder,
    private validatorService: ValidatorService,
    private passVerificationService: PasswordVerificationService
  ) {
    this.form = this.formBuilder.group(
      {
        password: ['', [Validators.required, Validators.pattern('^([a-zA-Z0-9]){6,}$')]],
        passwordConfirmation: ['', [Validators.required, Validators.pattern('^([a-zA-Z0-9]){6,}$')]],
      },
      {
        updateOn: 'change',
        validator: this.validatorService.matchingInputsValidator('password', 'passwordConfirmation'),
      }
    );

    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.form.controls['passwordConfirmation'].setAsyncValidators([this.asyncValidatePasswordPipe()]);
    this.form.controls['password'].setAsyncValidators([this.asyncValidatePasswordPipe()]);

    this.form.controls['passwordConfirmation'].statusChanges.subscribe((status) => {
      this.form.updateValueAndValidity();
    });

    this.form.controls['password'].statusChanges.subscribe((status) => {
      this.form.updateValueAndValidity();
    });
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  get value(): PasswordInfo {
    return this.form.value;
  }

  set value(value: PasswordInfo) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  get passwordControl() {
    return this.form.controls.password;
  }

  get passwordConfirmationControl() {
    return this.form.controls.passwordConfirmation;
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
      this.passwordInput.nativeElement.focus();
    }
  }

  validate(_: FormControl) {
    return this.form.valid ? null : { passwordInfo: { valid: false } };
  }

  private _asyncValidateEmailMatching(
    password: string,
    confirmPassword: string
  ): Observable<ValidationErrors | null> | null {
    return this.passVerificationService.verifyPasswordMatching(password, confirmPassword).pipe(
      map((result) => {
        if (result.response == false) {
          this.mismatchMsg = result.message;
          return {
            mismatch: true,
          };
        } else {
          return null;
        }
      })
    );
  }

  asyncValidatePasswordPipe(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        delay(300),
        switchMap((value) => {
          return this._asyncValidateEmailMatching(
            this.form.controls['password'].value || 'password',
            this.form.controls['passwordConfirmation'].value || 'passwordConfirmation'
          );
        }),
        switchMap((mismatch) => {
          if (mismatch) {
            this.passwordControl.setErrors({ mismatch: true });
            this.passwordConfirmationControl.setErrors({ mismatch: true });
            return of(mismatch);
          } else {
            this.passwordControl.setErrors(null);
            this.passwordConfirmationControl.setErrors(null);
            return of(null);
          }
        }),
        retryWithDelay(1000, 3),
        catchError((error: Error) => {
          show_popup(TITLE_ERROR, 'Lo sentimos, ocurrió un error al validar las contraseñas. Inténtalo de nuevo  P-67');
          this.passwordControl.setValue(null);
          this.passwordConfirmationControl.setValue(null);
          return EMPTY;
        })
      );
    };
  }
}
